// Reports what fails while the web app runs inside the iOS app's WebViews.
//
// Why: a TestFlight / App Store build keeps nothing the page logs. The host's
// console forwarder is DEBUG-only and its `print()`s go nowhere on a device, so
// "the proposal wizard failed on my phone" arrived with no trace. Inside the
// iOS host (a page loaded with `source=ios`, remembered for the tab, because
// the SPA drops the parameter as it navigates) this module:
//
//   - keeps a short history — the sign-in handover's `[iOS Auth]` lines, other
//     tagged logs, every warning and error — in sessionStorage, so it survives
//     the reload the handover does right after signing in;
//   - reports every `console.error`, uncaught error and unhandled rejection,
//     plus explicit `reportHostIssue` calls, to the `reportClientIssue` Cloud
//     Function, which writes each one to Cloud Logging;
//   - sends a report queued with `reportHostIssueOnNextPage` when the next
//     page in the tab installs (a slow sign-in handover, which ends in a
//     navigation that could cancel a request made before it).
//
// Outside the host it installs nothing. Inside, nothing is sent unless
// something fails or the sign-in handover was slow, at most `maxReports` distinct reports per page load, fire
// and forget, and no path through here can throw into the page.
// Reading reports: reference-docs/webview-resilience/overview.md.

import {
  REPORT_LIMITS,
  buildIssuePayload,
  firstError,
  formatConsoleArgs,
  isIgnoredMessage,
  pathOnly,
} from './issueReport';

const HOST_FLAG_KEY = 'hf.iosHost';
const CRUMBS_KEY = 'hf.iosHostCrumbs';
const PAGE_LOAD_KEY = 'hf.iosHostPageLoad';
const NEXT_PAGE_KEY = 'hf.iosHostNextPageReport';
const DEFAULT_MAX_REPORTS = 10;
const PERSIST_DELAY_MS = 1000;

// console.log / info lines worth keeping: the ones that narrate a flow.
const LOG_PREFIXES = [
  '[iOS Auth]',
  '[Auth]',
  '[Firebase]',
  '[proposal]',
  '[exitToHost]',
];

let state = null;

function sessionStorageOf(win) {
  try {
    return win.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function readSession(storage, key) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeSession(storage, key, value) {
  try {
    storage?.setItem(key, value);
  } catch {
    // Storage full or disabled: the history just won't survive a reload.
  }
}

/** Whether this page runs inside the iOS app. Sticky for the tab. */
export function detectIOSHost(win = window) {
  const storage = sessionStorageOf(win);
  let fromUrl = false;
  try {
    fromUrl = new URLSearchParams(win.location.search).get('source') === 'ios';
  } catch {
    fromUrl = false;
  }
  if (fromUrl) writeSession(storage, HOST_FLAG_KEY, '1');
  return fromUrl || readSession(storage, HOST_FLAG_KEY) === '1';
}

function loadCrumbs(storage) {
  try {
    const parsed = JSON.parse(readSession(storage, CRUMBS_KEY) || '[]');
    return Array.isArray(parsed)
      ? parsed.slice(-REPORT_LIMITS.breadcrumbs)
      : [];
  } catch {
    return [];
  }
}

function persistCrumbs() {
  if (!state) return;
  if (state.persistTimer !== null) {
    state.win.clearTimeout(state.persistTimer);
    state.persistTimer = null;
  }
  writeSession(state.storage, CRUMBS_KEY, JSON.stringify(state.crumbs));
}

function addCrumb(level, text) {
  if (!state || !text) return;
  state.crumbs.push({ t: Date.now(), level, text, page: state.pageLoad });
  if (state.crumbs.length > REPORT_LIMITS.breadcrumbs) {
    state.crumbs.splice(0, state.crumbs.length - REPORT_LIMITS.breadcrumbs);
  }
  // Batched: a render loop that warns every frame must not write storage
  // every frame. `pagehide` flushes whatever is pending.
  if (state.persistTimer === null) {
    state.persistTimer = state.win.setTimeout(persistCrumbs, PERSIST_DELAY_MS);
  }
}

/**
 * Report one failure. A no-op outside the iOS host, for a repeat of something
 * already reported from this page, and past the per-page cap.
 */
export function reportHostIssue(kind, { message, error, context } = {}) {
  if (!state) return false;
  try {
    const text =
      message ||
      (error ? formatConsoleArgs([error], REPORT_LIMITS.text) : '') ||
      kind;
    if (isIgnoredMessage(text)) return false;
    const key = `${kind}|${text.slice(0, 200)}`;
    if (state.sentKeys.has(key) || state.sentKeys.size >= state.maxReports) {
      return false;
    }
    state.sentKeys.add(key);

    const { win } = state;
    const payload = buildIssuePayload({
      kind,
      message: text,
      error,
      context,
      breadcrumbs: state.crumbs,
      href: win.location?.href,
      webBuild: win.__BUILD__?.shaShort ?? null,
      userAgent: win.navigator?.userAgent ?? null,
      hostDocuments: Boolean(win.webkit?.messageHandlers?.documentSaved),
      pageLoad: state.pageLoad,
    });
    addCrumb('report', `${kind}: ${payload.message}`);

    // A console.error raised while sending must not report itself.
    state.sending = true;
    try {
      Promise.resolve(state.send(payload)).catch(() => {});
    } catch {
      // A transport that throws is no different from one that rejects.
    } finally {
      state.sending = false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Queue a report for the next page load in this tab instead of sending it now:
 * for a page about to navigate (the handover's `window.location.replace`),
 * where a request in flight can be cancelled with the page. The reporter
 * sends it when it installs on that next page, with this page's history in
 * its breadcrumbs. One slot — a second call replaces the first. Returns
 * whether it was queued.
 */
export function reportHostIssueOnNextPage(kind, { message, context } = {}) {
  if (!state) return false;
  try {
    addCrumb('report', `${kind} (queued for next page): ${message ?? ''}`);
    persistCrumbs();
    writeSession(
      state.storage,
      NEXT_PAGE_KEY,
      JSON.stringify({ kind, message, context })
    );
    return true;
  } catch {
    return false;
  }
}

function sendQueuedReport() {
  const queued = readSession(state.storage, NEXT_PAGE_KEY);
  if (!queued) return;
  // Cleared before sending, so a page that fails while sending cannot loop.
  writeSession(state.storage, NEXT_PAGE_KEY, '');
  try {
    const { kind, message, context } = JSON.parse(queued);
    reportHostIssue(kind, { message, context });
  } catch {
    // A corrupt entry is dropped, never retried.
  }
}

function observeConsole(level, args) {
  if (level === 'log' || level === 'info') {
    const first = args[0];
    if (
      typeof first === 'string' &&
      LOG_PREFIXES.some((prefix) => first.startsWith(prefix))
    ) {
      addCrumb(level, formatConsoleArgs(args));
    }
    return;
  }
  if (level === 'warn') {
    addCrumb('warn', formatConsoleArgs(args));
    return;
  }
  // error
  const text = formatConsoleArgs(args, REPORT_LIMITS.text);
  if (state.sending || isIgnoredMessage(text)) return;
  reportHostIssue('console-error', { message: text, error: firstError(args) });
}

function wrapConsole(consoleObject) {
  ['log', 'info', 'warn', 'error'].forEach((level) => {
    const original = consoleObject[level];
    if (typeof original !== 'function') return;
    consoleObject[level] = function hostIssueConsole(...args) {
      try {
        observeConsole(level, args);
      } catch {
        // Diagnostics must never break the caller.
      }
      return original.apply(this, args);
    };
    state.cleanup.push(() => {
      consoleObject[level] = original;
    });
  });
}

function listen(win, type, handler) {
  win.addEventListener(type, handler);
  state.cleanup.push(() => win.removeEventListener(type, handler));
}

/**
 * Install once, before the app renders (index.js). Returns whether it
 * installed — false outside the iOS host.
 *
 * @param {{ send: (payload: object) => Promise<unknown>, win?: Window, maxReports?: number }} options
 */
export function installHostIssueReporter({
  send,
  win = window,
  maxReports = DEFAULT_MAX_REPORTS,
} = {}) {
  if (state) return true;
  try {
    if (!win || typeof send !== 'function' || !detectIOSHost(win)) return false;
    const storage = sessionStorageOf(win);
    const pageLoad = Number(readSession(storage, PAGE_LOAD_KEY) || 0) + 1;
    writeSession(storage, PAGE_LOAD_KEY, String(pageLoad));

    state = {
      win,
      send,
      storage,
      maxReports,
      pageLoad,
      crumbs: loadCrumbs(storage),
      sentKeys: new Set(),
      sending: false,
      persistTimer: null,
      cleanup: [],
    };

    wrapConsole(win.console);
    listen(win, 'error', (event) => {
      reportHostIssue('uncaught-error', {
        message: event?.message,
        error: event?.error instanceof Error ? event.error : null,
        context: event?.filename
          ? {
              source: pathOnly(event.filename),
              line: event.lineno,
              column: event.colno,
            }
          : undefined,
      });
    });
    listen(win, 'unhandledrejection', (event) => {
      const reason = event?.reason;
      reportHostIssue('unhandled-rejection', {
        message: formatConsoleArgs([reason], REPORT_LIMITS.text),
        error: reason instanceof Error ? reason : null,
      });
    });
    listen(win, 'pagehide', persistCrumbs);

    addCrumb('info', `page load ${pageLoad}: ${pathOnly(win.location?.href)}`);
    sendQueuedReport();
    return true;
  } catch {
    state = null;
    return false;
  }
}

/** Tests only: undo `installHostIssueReporter`. */
export function uninstallHostIssueReporter() {
  if (!state) return;
  if (state.persistTimer !== null) state.win.clearTimeout(state.persistTimer);
  state.cleanup.forEach((undo) => undo());
  state = null;
}
